import i18n from "i18next";
import { type FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Loading, PopupWindow } from "src/components";
import { RequestError } from "src/errorHandling";
import { Routes } from "src/routes";
import {
    useEvaluateInvestorQuestionnaireMutation,
    useInvestorQuestionnaireDefinitionQuery,
    useLazyUserInvestorQuestionnaireQuery,
    useUserInvestorQuestionnaireQuery,
    useUserQuery,
} from "src/serverApi";
import { useTranslation } from "src/translations";
import type { WarningConfirmation, WarningConfirmationType } from "src/types";
import type { QuestionnaireAnswers } from "./hooks/useQuestionnaireAnswers";
import { useQuestionnaireAnswers } from "./hooks/useQuestionnaireAnswers";
import { groupQuestions, isGroupValid } from "./hooks/useQuestionnaireGroups";
import { getLatestSubmissionWarnings } from "./hooks/useQuestionnaireWarnings";
import { QuestionnaireInfo } from "./QuestionnaireInfo";
import { QuestionnaireResult } from "./QuestionnaireResult";
import { QuestionnaireStep } from "./QuestionnaireStep";

type ResultState =
    | { type: "APPROVED" }
    | { type: "REJECTED" }
    | {
          type: "WARNING";
          documentId: string;
          warningCodes: WarningConfirmationType[];
          existingConfirmations: WarningConfirmation[];
      };

function buildPayload(answers: QuestionnaireAnswers) {
    return {
        language: i18n.language,
        questionsAndAnswers: Object.entries(answers)
            .filter(([, value]) => Array.isArray(value) || Boolean(value))
            .map(([questionId, value]) => ({
                questionId,
                answerIds: Array.isArray(value) ? value : [value],
            })),
    };
}

export const InvestorQuestionnaire: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isConfirmingWarnings = searchParams.get("confirmWarnings") === "true";
    const projectSlug = searchParams.get("slug");
    const navigateToProjects = () => navigate(projectSlug ? Routes.projectOrder.fillPathParams({ projectSlug }) : Routes.projects.path);
    const { data: questionnaire, isLoading, isError, error } = useInvestorQuestionnaireDefinitionQuery();
    const { data: user } = useUserQuery();
    const [evaluateQuestionnaire, { isLoading: isSubmitting }] = useEvaluateInvestorQuestionnaireMutation();
    const [fetchUserInvestorQuestionnaire] = useLazyUserInvestorQuestionnaireQuery();
    const { data: existingUserQuestionnaire, isLoading: isExistingQuestionnaireLoading } = useUserInvestorQuestionnaireQuery(
        user?.id as number,
        { skip: !user?.id || !isConfirmingWarnings },
    );

    const { answers, setAnswer, clearAnswers, clearAll } = useQuestionnaireAnswers();
    const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
    const [showErrors, setShowErrors] = useState(false);
    const [showInfo, setShowInfo] = useState(true);
    const [result, setResult] = useState<ResultState | null>(null);
    const [isResolvingResult, setIsResolvingResult] = useState(false);

    const existingOutcome = existingUserQuestionnaire?.lastQuestionnaireOutcome;
    const hasPendingWarnings =
        isConfirmingWarnings && existingOutcome?.decisionStatus === "WARNING" && existingOutcome?.requiresWarningConfirmation === true;

    // Confirming a warning re-fetches the questionnaire and can flip `hasPendingWarnings` to false
    // mid-flow. Snapshot the data once we see pending warnings so this screen keeps showing until
    // the user clicks "Continue" themselves, instead of falling through to the intro screen below.
    const [pendingWarningsResult, setPendingWarningsResult] = useState<Extract<ResultState, { type: "WARNING" }> | null>(null);
    useEffect(() => {
        if (!pendingWarningsResult && hasPendingWarnings && existingUserQuestionnaire?.documentId) {
            setPendingWarningsResult({
                type: "WARNING",
                documentId: existingUserQuestionnaire.documentId,
                warningCodes: existingOutcome?.warningCodes ?? [],
                existingConfirmations: getLatestSubmissionWarnings(existingUserQuestionnaire),
            });
        }
    }, [pendingWarningsResult, hasPendingWarnings, existingUserQuestionnaire, existingOutcome]);

    if (isLoading || isExistingQuestionnaireLoading) return <Loading />;
    if (isError) throw new RequestError("Cannot load investor questionnaire", error);
    if (!questionnaire?.questions?.length) throw new RequestError("Investor questionnaire is empty");

    if (pendingWarningsResult) {
        return (
            <section className="mx-auto mt-6 flex w-full max-w-[39.5rem] flex-col gap-4 p-2">
                <QuestionnaireResult {...pendingWarningsResult} onContinue={navigateToProjects} />
            </section>
        );
    }

    if (showInfo) {
        return (
            <section className="mx-auto mt-6 flex w-full max-w-[39.5rem] flex-col gap-4 p-2">
                <QuestionnaireInfo onStart={() => setShowInfo(false)} />
            </section>
        );
    }

    if (result) {
        return (
            <section className="mx-auto mt-6 flex w-full max-w-[39.5rem] flex-col gap-4 p-2">
                <QuestionnaireResult {...result} onContinue={navigateToProjects} />
            </section>
        );
    }

    const groups = groupQuestions(questionnaire.questions);
    const currentGroup = groups[currentGroupIndex];
    const isFirst = currentGroupIndex === 0;
    const isLast = currentGroupIndex === groups.length - 1;

    const handleRootChange = (value: string | string[]) => {
        const valueArray = Array.isArray(value) ? value : [value];
        const toBeCleared = currentGroup.conditionals.filter((q) => !valueArray.includes(q.displayCondition)).map((q) => q.questionId);
        if (toBeCleared.length > 0) clearAnswers(toBeCleared);
        setAnswer(currentGroup.root.questionId, value);
        setShowErrors(false);
    };

    const handleConditionalChange = (questionId: string, value: string | string[]) => {
        setAnswer(questionId, value);
        setShowErrors(false);
    };

    const submitQuestionnaire = async (userId: number) => {
        setIsResolvingResult(true);
        const response = await evaluateQuestionnaire(buildPayload(answers));
        if ("error" in response) {
            setIsResolvingResult(false);
            const { isConfirmed } = await PopupWindow.fire({
                title: t("verification.INVESTOR_QUESTIONNAIRE.submitError.title"),
                text: t("verification.INVESTOR_QUESTIONNAIRE.submitError.text"),
                confirmButtonText: t("verification.INVESTOR_QUESTIONNAIRE.submitError.retry"),
                cancelButtonText: t("verification.INVESTOR_QUESTIONNAIRE.submitError.cancel"),
                showCancelButton: true,
            });
            if (isConfirmed) {
                await submitQuestionnaire(userId);
            } else {
                navigate(Routes.settings.path);
            }
            return;
        }

        clearAll();

        if (response.data.result === "APPROVED") {
            setResult({ type: "APPROVED" });
            setIsResolvingResult(false);
            return;
        }

        if (response.data.result === "REJECTED") {
            setResult({ type: "REJECTED" });
            setIsResolvingResult(false);
            return;
        }

        if (response.data.result === "WARNING") {
            const userQuestionnaire = await fetchUserInvestorQuestionnaire(userId);
            if (!userQuestionnaire.data?.documentId) {
                navigate(Routes.settings.path);
                setIsResolvingResult(false);
                return;
            }
            setResult({
                type: "WARNING",
                documentId: userQuestionnaire.data.documentId,
                warningCodes: userQuestionnaire.data.lastQuestionnaireOutcome?.warningCodes ?? [],
                existingConfirmations: getLatestSubmissionWarnings(userQuestionnaire.data),
            });
            setIsResolvingResult(false);
        }
    };

    const handleNext = async () => {
        if (!isGroupValid(currentGroup, answers)) {
            setShowErrors(true);
            return;
        }
        setShowErrors(false);
        if (!isLast) {
            setCurrentGroupIndex((i) => i + 1);
            return;
        }
        if (!user?.id) return;
        await submitQuestionnaire(user.id);
    };

    const handlePrev = () => {
        setShowErrors(false);
        setCurrentGroupIndex((i) => i - 1);
    };

    if (isSubmitting || isResolvingResult) return <Loading />;

    return (
        <section className="mx-auto mt-6 flex w-full max-w-[39.5rem] flex-col gap-4 p-2">
            <QuestionnaireStep
                group={currentGroup}
                groupIndex={currentGroupIndex}
                totalGroups={groups.length}
                answers={answers}
                onRootChange={handleRootChange}
                onConditionalChange={handleConditionalChange}
                onNext={handleNext}
                onPrev={handlePrev}
                isFirst={isFirst}
                isLast={isLast}
                isSubmitting={isSubmitting}
                showErrors={showErrors}
            />
        </section>
    );
};
